/**
 * v1.3.26 — WarmupBanner
 * ------------------------------------------------------------------
 * Soft "instance is warming up" banner. Renders whenever the backend
 * reports `complete: false` on /api/system/warmup-status. Auto-hides
 * as soon as the backend flips to `complete: true`.
 *
 * When does this fire?
 *   The pod tells k8s "I'm ready" the moment the /health endpoint
 *   responds. But the app then runs ~30-90s of deferred background
 *   work (index builds, migrations, golden reference sync, scheduler
 *   warm-up). During that window a user hitting the app can see
 *   transient missing fields that come back on refresh. The banner
 *   tells them that's normal and to hold off on urgent edits.
 *
 * Design intent — DELIBERATELY tiny and low-key:
 *   - No blocking modal, no red alarm colour, no dismiss button
 *     (auto-dismisses when done). Amber pill top-right of the page.
 *   - Poll every 4 seconds while `complete === false`, stop polling
 *     the moment it flips true or after a hard 5-minute ceiling.
 *   - Never shown once complete on a given page load (component
 *     unmounts its poll after the first `complete: true` response).
 *
 * Zero risk to existing flows: additive component, mounted at the
 * top of App.js next to <ImpersonationBanner />. Uses no context,
 * no shared state, no localStorage.
 */
import { useEffect, useState, useRef } from 'react';
import { Loader2 } from 'lucide-react';

const POLL_INTERVAL_MS = 4000;
const HARD_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes ceiling — banner disappears no matter what

export function WarmupBanner() {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [complete, setComplete] = useState(null); // null = first check pending
  const [elapsed, setElapsed] = useState(0);
  const startedAt = useRef(Date.now());
  const cancelled = useRef(false);

  useEffect(() => {
    if (!API) { setComplete(true); return; }
    cancelled.current = false;

    let timer;
    const check = async () => {
      if (cancelled.current) return;
      // Hard timeout — never keep polling forever.
      if (Date.now() - startedAt.current > HARD_TIMEOUT_MS) {
        setComplete(true);
        return;
      }
      try {
        const res = await fetch(`${API}/api/system/warmup-status`, {
          method: 'GET',
          cache: 'no-store',
        });
        if (!res.ok) {
          // Warmup endpoint 404s on very old backends (<v1.3.26).
          // Treat that as "already complete" — the banner is opt-in,
          // never show if the backend can't answer.
          if (res.status === 404) { setComplete(true); return; }
          // Any other transient failure — try again on the next tick.
          timer = setTimeout(check, POLL_INTERVAL_MS);
          return;
        }
        const data = await res.json();
        setComplete(!!data?.complete);
        setElapsed(data?.elapsed_seconds || 0);
        if (data?.complete) return; // stop polling
        timer = setTimeout(check, POLL_INTERVAL_MS);
      } catch {
        // Network error — the very error the banner is meant to warn
        // about. Show the banner (complete === false), try again.
        setComplete(false);
        timer = setTimeout(check, POLL_INTERVAL_MS);
      }
    };
    check();
    return () => { cancelled.current = true; clearTimeout(timer); };
  }, [API]);

  // Never render on first paint (avoid a flicker before the first
  // status response) OR when the backend says complete.
  if (complete === null || complete === true) return null;

  return (
    <div
      className="fixed top-3 right-3 z-[9999] flex items-center gap-2 px-3 py-2
                 bg-amber-50 border border-amber-300 text-amber-900 rounded-md
                 shadow-sm text-xs max-w-xs"
      data-testid="warmup-banner"
      role="status"
      aria-live="polite"
    >
      <Loader2 size={14} className="animate-spin text-amber-700 flex-shrink-0" />
      <div className="leading-tight">
        <div className="font-semibold">Instance warming up</div>
        <div className="text-[11px] text-amber-800">
          Backend finalising for another minute. If a field looks empty, refresh once — data returns as sync completes.
          {elapsed > 5 && <span className="ml-1 opacity-60">({elapsed}s)</span>}
        </div>
      </div>
    </div>
  );
}

export default WarmupBanner;
